import { useState, useCallback } from 'react';
import { useAudioGeneration } from '@/hooks/useAudioGeneration';
import { useToast } from '@/hooks/use-toast';

interface EnhancedGenerationState {
  currentStep: 'voice-validation' | 'content-generation' | 'audio-synthesis' | 'auto-save' | null;
  preservedContent: string | null;
  hasCompletedContent: boolean;
  detailedError: string | null;
}

export const useEnhancedSessionGeneration = () => {
  const [enhancedState, setEnhancedState] = useState<EnhancedGenerationState>({
    currentStep: null,
    preservedContent: null,
    hasCompletedContent: false,
    detailedError: null
  });

  const { generateAudioFromPrompt, isGenerating, audioUrl, sessionId, error, generatedContent, resetAudio } = useAudioGeneration();
  const { toast } = useToast();

  const enhancedGenerateAudioFromPrompt = useCallback(async (
    prompt: string,
    useExactText = false,
    includeBiography = false,
    userBiography = '',
    categoryId?: string,
    voiceGender?: string,
    sessionTitle?: string,
    voiceType?: string,
    sessionType?: string,
    latencyTolerance?: number,
    groupSession?: any,
    usePersonalBiography?: boolean,
    practitionerClient?: any,
    specific_voice_id?: string,
    practitionerProfile?: any,
    fastMode?: boolean,
    backgroundMusicFile?: string
  ) => {
    setEnhancedState(prev => ({
      ...prev,
      currentStep: 'voice-validation',
      detailedError: null,
      preservedContent: null,
      hasCompletedContent: false
    }));

    try {
      // Step 1: Voice Validation
      console.log('🎤 Step 1: Voice validation starting...');

      if (specific_voice_id) {
        const { validateVoiceSelection } = await import('@/utils/voiceValidation');
        const voiceValidation = validateVoiceSelection(specific_voice_id);

        if (!voiceValidation.isValid) {
          throw new Error(`Voice "${specific_voice_id}" not found. Available voices: ${voiceValidation.debug.availableMaleVoices.concat(voiceValidation.debug.availableFemaleVoices).map(v => v.id).join(', ')}`);
        }

        console.log('✅ Voice validation passed:', voiceValidation.matchedVoice?.name);
      }

      // Step 2: Content Generation
      setEnhancedState(prev => ({ ...prev, currentStep: 'content-generation' }));
      console.log('📝 Step 2: Content generation starting...');

      // Step 3: Audio Synthesis  
      setEnhancedState(prev => ({ ...prev, currentStep: 'audio-synthesis' }));
      console.log('🔊 Step 3: Audio synthesis starting...');

      await generateAudioFromPrompt(
        prompt,
        useExactText,
        includeBiography,
        userBiography,
        categoryId,
        voiceGender,
        sessionTitle,
        voiceType,
        sessionType,
        latencyTolerance,
        groupSession,
        usePersonalBiography,
        practitionerClient,
        specific_voice_id,
        practitionerProfile,
        fastMode,
        backgroundMusicFile
      );

      // Step 4: Success
      setEnhancedState(prev => ({ 
        ...prev, 
        currentStep: 'auto-save',
        hasCompletedContent: true,
        preservedContent: generatedContent
      }));
      
      console.log('✅ All steps completed successfully');

    } catch (error: any) {
      console.error('❌ Enhanced generation failed at step:', enhancedState.currentStep, error);
      
      // Preserve content if it was generated before audio failed
      if (generatedContent && enhancedState.currentStep === 'audio-synthesis') {
        setEnhancedState(prev => ({
          ...prev,
          preservedContent: generatedContent,
          hasCompletedContent: true,
          detailedError: `Audio generation failed: ${error.message}. Content has been preserved.`
        }));
        
        toast({
          title: "Partial Success",
          description: "Content generated but audio failed. You can try audio generation again.",
          variant: "destructive",
        });
      } else {
        setEnhancedState(prev => ({
          ...prev,
          detailedError: error.message,
          currentStep: null
        }));
      }
      
      throw error;
    }
  }, [generateAudioFromPrompt, generatedContent, enhancedState.currentStep, toast]);

  const resetEnhancedAudio = useCallback(() => {
    resetAudio();
    setEnhancedState({
      currentStep: null,
      preservedContent: null,
      hasCompletedContent: false,
      detailedError: null
    });
  }, [resetAudio]);

  return {
    // Original hook properties
    isGenerating,
    audioUrl,
    sessionId, // Backend-created session ID
    error,
    generatedContent: generatedContent || enhancedState.preservedContent,

    // Enhanced properties
    currentStep: enhancedState.currentStep,
    hasCompletedContent: enhancedState.hasCompletedContent,
    preservedContent: enhancedState.preservedContent,
    detailedError: enhancedState.detailedError,

    // Methods
    generateAudioFromPrompt: enhancedGenerateAudioFromPrompt,
    resetAudio: resetEnhancedAudio
  };
};